// src/components/enterprise/RoiCalculator.jsx
//
// The financial-impact tab. Every number here comes from the executive
// metric layer (src/utils/executiveMetrics.js):
//   - the organization's ERI (live snapshot) drives the readiness engine and
//     scales the expected-claims term;
//   - the organization's own business inputs — typed here and saved to the
//     organization, or uploaded through the data integration layer — feed the
//     five-term model;
//   - business averages fill any gap and draw the comparison line.
// Client figures, uploads and benchmarks are labelled as such on every field
// and every term. This is a measurement model, not a savings promise.

import React, { useEffect, useMemo, useState } from 'react';
import {
    Calculator, TrendingDown, DollarSign, Activity, PieChart,
    Building2, BarChart2, Lock, ShieldCheck, Info, FileSpreadsheet,
    Code2, Plug, PencilLine, AlertTriangle, Sigma, Gauge, Save, Scale
} from 'lucide-react';
import { useEdition } from '../../data/editions/EditionContext';
import {
    computeExecutiveMetrics, makeFormatters, INPUT_FIELDS, BENCH_FIELDS,
    ADOPTION_SCENARIOS, ATTRIBUTION_MIN, ATTRIBUTION_MAX, DEFAULT_ATTRIBUTION
} from '../../utils/executiveMetrics';

/* ------------------------------------------------------------------ */
/*  Small presentational atoms                                         */
/* ------------------------------------------------------------------ */

const SOURCE_STYLES = {
    client:    ['Client data',  'text-[var(--brand-deep)] bg-blue-50 border-blue-100'],
    observed:  ['Observed',     'text-[var(--brand-deep)] bg-blue-50 border-blue-100'],
    upload:    ['Uploaded',     'text-emerald-700 bg-emerald-50 border-emerald-100'],
    benchmark: ['Benchmark',    'text-amber-700 bg-amber-50 border-amber-100'],
    modeled:   ['Modeled',      'text-amber-700 bg-amber-50 border-amber-100'],
    missing:   ['Needed',       'text-rose-700 bg-rose-50 border-rose-100'],
    none:      ['Illustrative', 'text-slate-500 bg-slate-50 border-slate-200']
};

const SourceBadge = ({ source }) => {
    const [label, cls] = SOURCE_STYLES[source] || SOURCE_STYLES.modeled;
    return <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}>{label}</span>;
};

const ConfidenceTag = ({ level }) => {
    const map = {
        Leading:      'text-emerald-700 bg-emerald-50 border-emerald-100',
        Intermediate: 'text-[var(--brand-deep)] bg-blue-50 border-blue-100',
        Lagging:      'text-amber-700 bg-amber-50 border-amber-100'
    };
    return <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${map[level]}`}>{level}</span>;
};

const Field = ({ label, value, onChange, unit, prefix, step = 1, disabled, badge, required, placeholder }) => (
    <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                {label}
                {required && <span className="text-[9px] font-bold text-rose-500 uppercase tracking-wider">Required</span>}
            </label>
            {badge}
        </div>
        <div className={`flex items-center rounded-lg border bg-white transition-colors ${
            disabled ? 'border-slate-100 bg-slate-50' : 'border-slate-200 focus-within:border-[var(--brand-blue)]'
        }`}>
            {prefix && <span className="pl-3 text-sm font-medium text-slate-400">{prefix}</span>}
            <input
                type="number"
                step={step}
                value={value ?? ''}
                placeholder={placeholder}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent px-3 py-2 text-sm font-semibold text-slate-800 outline-none disabled:text-slate-400"
            />
            {unit && <span className="pr-3 text-xs font-medium text-slate-400 whitespace-nowrap">{unit}</span>}
        </div>
    </div>
);

const SignalGauge = ({ label, value, suffix = '' }) => (
    <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-300">{label}</span>
            <span className="text-sm font-semibold text-white tabular-nums">{typeof value === 'number' ? `${value}${suffix}` : '—'}</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-1">
            <div className="bg-[var(--brand-blue)] h-1 rounded-full transition-all duration-700" style={{ width: `${typeof value === 'number' ? Math.max(0, Math.min(100, value)) : 0}%` }} />
        </div>
    </div>
);

const draftFrom = (saved) => {
    const s = saved || {};
    return {
        eligibleEmployees: s.eligibleEmployees ?? '',
        loadedLabourCost: s.loadedLabourCost ?? '',
        absenceDays: s.absenceDays ?? '',
        turnoverRate: s.turnoverRate ?? '',
        efapUtilization: s.efapUtilization ?? '',
        programPEPM: s.programPEPM ?? '',
        useBenchmark: {
            absenceDays: !!(s.useBenchmark && s.useBenchmark.absenceDays),
            turnoverRate: !!(s.useBenchmark && s.useBenchmark.turnoverRate),
            efapUtilization: !!(s.useBenchmark && s.useBenchmark.efapUtilization)
        },
        attribution: s.attribution ?? DEFAULT_ATTRIBUTION
    };
};

const num = (val) => { const n = parseFloat(val); return isNaN(n) ? 0 : n; };

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const RoiCalculator = ({ live, savedInputs, onSave }) => {
    // Edition overrides only the bits that differ by vertical (currency/locale,
    // replacement multiple, turnover benchmark, term labels). The component is
    // keyed by edition.id in EnterpriseConsole, so state re-seeds on a switch.
    const edition = useEdition();
    const roi = edition.roi || {};
    const { compact } = useMemo(() => makeFormatters(roi.locale, roi.currency), [roi.locale, roi.currency]);
    const isLive = live?.source === 'live';

    const [draft, setDraft] = useState(() => draftFrom(savedInputs));
    const savedStamp = savedInputs?.updatedAt || null;
    // Re-seed when a save lands (from this or another admin's session).
    useEffect(() => { setDraft(draftFrom(savedInputs)); }, [savedStamp]); // eslint-disable-line react-hooks/exhaustive-deps

    const [benchOverrides, setBenchOverrides] = useState({});
    const [scenario, setScenario] = useState('Expected');
    const [showBench, setShowBench] = useState(false);
    const [dataMode, setDataMode] = useState('manual');
    const [saveState, setSaveState] = useState({ busy: false, error: null, at: null });

    const M = useMemo(() => computeExecutiveMetrics({
        live, edition, saved: draft, uploaded: live?.business, benchOverrides,
        attribution: draft.attribution, scenario, windowDays: live?.windowDays || 90
    }), [live, edition, draft, benchOverrides, scenario]);
    const { inputs, bench, readiness, cohort, outlook, financial: result, compare } = M;

    const setC = (key) => (val) => setDraft((d) => ({ ...d, [key]: val }));
    const setB = (key) => (val) => setBenchOverrides((b) => ({ ...b, [key]: val }));
    const toggleBench = (key) => setDraft((d) => ({ ...d, useBenchmark: { ...d.useBenchmark, [key]: !d.useBenchmark[key] } }));
    const dirty = JSON.stringify(draft) !== JSON.stringify(draftFrom(savedInputs));

    // What the field shows: the admin's draft, else the resolved value
    // (uploaded / benchmark / modeled) — editing it makes it client data.
    const shown = (key) => (draft[key] === '' || draft[key] === null || draft[key] === undefined) ? (inputs[key]?.value ?? '') : draft[key];

    const handleSave = async () => {
        if (!onSave) return;
        setSaveState({ busy: true, error: null, at: null });
        try {
            await onSave(draft);
            setSaveState({ busy: false, error: null, at: new Date() });
        } catch (e) {
            setSaveState({ busy: false, error: e?.message || 'Save failed', at: null });
        }
    };

    const terms = [
        {
            key: 'lostTime', label: (roi.termLabels && roi.termLabels.lostTime) || 'Avoided Lost Time', icon: TrendingDown,
            value: result.avoidedLostTime, level: 'Leading', source: inputs.absenceDays.source,
            basis: (roi.termLabels && roi.termLabels.lostTimeBasis) || 'Baseline absence days × loaded daily cost × modeled reduction.'
        },
        {
            key: 'turnover', label: 'Avoided Turnover', icon: Building2,
            value: result.avoidedTurnover, level: 'Lagging', source: inputs.turnoverRate.source,
            basis: 'Voluntary separations averted × conservative replacement cost.'
        },
        {
            key: 'benefits', label: 'Benefits Utilization Lift', icon: Activity,
            value: result.benefitsLift, level: 'Intermediate', source: inputs.efapUtilization.source,
            basis: 'Earlier routing into existing EAP / paramedical benefits already paid for.'
        },
        {
            key: 'risk', label: 'Risk Reduction', icon: ShieldCheck,
            value: result.riskReduction, level: 'Lagging', source: readiness.source === 'live' ? 'modeled' : 'benchmark',
            basis: readiness.source === 'live'
                ? `Expected claims at the business-average incidence, scaled ${outlook.riskMultiplier}× by this organization's ERI, × modeled reduction. Needs a 6–12 month window + baseline to confirm.`
                : 'Modeled STD/LTD claims averted — needs a 6–12 month window + baseline to confirm.'
        }
    ];

    const hasCost = result.programCost > 0;
    const runningHotPct = typeof cohort.runningHotShare === 'number' ? Math.round(cohort.runningHotShare * 100) : null;

    const dataModes = [
        { id: 'manual', label: 'By hand', icon: PencilLine },
        { id: 'csv', label: 'CSV', icon: FileSpreadsheet },
        { id: 'xml', label: 'XML', icon: Code2 },
        { id: 'api', label: 'API', icon: Plug }
    ];

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 no-print">

            {/* ============================================================ */}
            {/* HERO — live Net ROI, framed as a measurement model           */}
            {/* ============================================================ */}
            <div className="bg-slate-900 rounded-xl overflow-hidden shadow-md border border-slate-800 relative">
                <div className="p-8 md:p-10 flex flex-col lg:flex-row gap-10 items-center relative z-10">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <DollarSign className="text-emerald-400" size={20} strokeWidth={2} />
                            <h2 className="text-sm font-semibold text-slate-300 tracking-widest uppercase">
                                {hasCost ? 'Modelled Return on Program Cost' : 'Modelled Annual Value Identified'}
                            </h2>
                            <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 bg-white/5 border border-white/10 px-2 py-0.5 rounded">
                                Measurement model · not a guarantee
                            </span>
                        </div>
                        {result.complete ? (
                            <>
                                <div className="flex items-baseline gap-3 mb-2">
                                    <span className="text-5xl md:text-6xl font-semibold text-white tracking-tight">
                                        {hasCost ? `${result.ratio.toFixed(1)}×` : compact(result.grossValue)}
                                    </span>
                                    <span className="text-sm font-medium text-slate-400 uppercase tracking-wider">
                                        {hasCost ? 'Return multiple' : '/ Annualized'}
                                    </span>
                                </div>
                                <p className="text-xs text-slate-400 font-medium mb-4">
                                    {hasCost
                                        ? `${compact(result.netRoi)} net annual value · net of ${compact(result.programCost)} indicative program cost`
                                        : 'Enter an indicative program cost below to model net ROI and the return multiple.'}
                                </p>
                            </>
                        ) : (
                            <div className="mb-4">
                                <span className="text-4xl font-semibold text-white tracking-tight">—</span>
                                <p className="text-xs text-amber-300 font-medium mt-2">Add eligible employees and loaded labour cost below to model this organization&apos;s value.</p>
                            </div>
                        )}
                        <div className="flex items-center gap-2 text-[11px] text-slate-400 font-light bg-white/5 border border-white/10 rounded-lg px-4 py-3 max-w-xl">
                            <Sigma size={26} className="text-emerald-400 shrink-0" strokeWidth={1.75} />
                            <span className="leading-relaxed">
                                <strong className="text-slate-200 font-medium">Net ROI</strong> = {terms[0].label} + Avoided Turnover
                                + Benefits Utilization Lift + Risk Reduction − EAS Program Cost, each discounted by an ERI-adjusted attribution factor.
                                Presenteeism is tracked as a leading indicator, not added to the headline.
                            </span>
                        </div>
                    </div>

                    {/* Value composition */}
                    <div className="w-full lg:w-[380px] bg-white/5 backdrop-blur-md p-6 rounded-lg border border-white/10 shrink-0">
                        <h3 className="text-emerald-400 font-medium text-[11px] uppercase tracking-widest mb-5 flex items-center gap-2">
                            <PieChart size={14} /> Value Composition
                        </h3>
                        <div className="space-y-4">
                            {terms.map((t) => {
                                const p = result.grossValue > 0 ? Math.max(2, Math.round((t.value / result.grossValue) * 100)) : 0;
                                return (
                                    <div key={t.key}>
                                        <div className="flex justify-between text-xs mb-1.5 text-slate-200 font-light">
                                            <span>{t.label}</span>
                                            <span className="font-semibold text-white">{compact(t.value)}</span>
                                        </div>
                                        <div className="w-full bg-slate-800 rounded-full h-1">
                                            <div className="bg-emerald-400 h-1 rounded-full transition-all duration-700" style={{ width: `${p}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* INPUTS — company-specific + the readiness engine             */}
            {/* ============================================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Company-specific inputs */}
                <div className="bg-white p-6 md:p-7 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-start justify-between mb-1 gap-3">
                        <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-lg bg-blue-50 text-[var(--brand-deep)] flex items-center justify-center">
                                <Building2 size={18} strokeWidth={2} />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-[var(--brand-deep)]">Company Inputs</h3>
                                <p className="text-[11px] text-slate-400 font-medium">Client-provided figures carry the highest credibility · uploads next · benchmarks fill the gaps</p>
                            </div>
                        </div>
                        {onSave && (
                            <button
                                onClick={handleSave}
                                disabled={!dirty || saveState.busy}
                                className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${
                                    dirty ? 'bg-[var(--brand-deep)] text-white hover:opacity-90' : 'bg-slate-100 text-slate-400'
                                }`}
                            >
                                <Save size={13} /> {saveState.busy ? 'Saving…' : 'Save to organization'}
                            </button>
                        )}
                    </div>
                    {(saveState.error || saveState.at || savedStamp) && (
                        <p className={`text-[11px] mt-1 ${saveState.error ? 'text-rose-600' : 'text-slate-400'}`}>
                            {saveState.error
                                ? saveState.error
                                : `Saved ${new Date(saveState.at || savedStamp).toLocaleString()}${savedInputs?.updatedBy ? ` by ${savedInputs.updatedBy}` : ''} · shared with every admin of this organization`}
                        </p>
                    )}

                    {/* Ingestion mode selector — ties to the Data Integration panel */}
                    <div className="flex items-center gap-1 mt-4 mb-5 p-1 bg-slate-100 rounded-lg w-fit">
                        {dataModes.map((m) => {
                            const Icon = m.icon;
                            return (
                                <button
                                    key={m.id}
                                    onClick={() => setDataMode(m.id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                        dataMode === m.id ? 'bg-white text-[var(--brand-deep)] shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                    }`}
                                >
                                    <Icon size={13} /> {m.label}
                                </button>
                            );
                        })}
                    </div>

                    {dataMode !== 'manual' && (
                        <div className="mb-5 flex items-start gap-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 leading-relaxed">
                            <Info size={14} className="shrink-0 mt-0.5 text-[var(--brand-blue)]" />
                            <span>
                                {dataMode === 'csv' && 'CSV uploads land in the warehouse through the secure Data Integration layer; the latest value for headcount, absence days, turnover and EAP utilization auto-fills these fields (labelled Uploaded) until an admin types a figure.'}
                                {dataMode === 'xml' && 'XML feed mapping is configured post-pilot through the secure ingestion layer.'}
                                {dataMode === 'api' && 'Direct API automation is configured post-pilot and requires IT authorization. Values then sync automatically.'}
                            </span>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {INPUT_FIELDS.map((f) => (
                            <Field
                                key={f.key}
                                label={f.label}
                                required={f.required}
                                prefix={f.prefix}
                                unit={f.unit}
                                step={f.step}
                                value={shown(f.key)}
                                placeholder={inputs[f.key].source === 'missing' ? 'required' : undefined}
                                onChange={setC(f.key)}
                                disabled={!!draft.useBenchmark[f.key]}
                                badge={<SourceBadge source={inputs[f.key].source} />}
                            />
                        ))}
                        <Field label="Indicative program cost" prefix="$" unit="PEPM" step={0.5}
                            value={draft.programPEPM} onChange={setC('programPEPM')}
                            badge={<SourceBadge source="none" />} />
                    </div>

                    <p className="text-[11px] text-slate-500 font-light leading-relaxed mt-4 flex items-start gap-1.5">
                        <Info size={13} className="text-slate-400 shrink-0 mt-0.5" />
                        Program cost is illustrative only and isn&apos;t a quote — pricing is confirmed separately. Leave it blank to show modelled value identified.
                    </p>

                    <div className="mt-5 pt-4 border-t border-slate-100">
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Substitute the business average where the client has no baseline</p>
                        <div className="flex flex-wrap gap-2">
                            {[
                                { key: 'absenceDays', label: 'Absence days' },
                                { key: 'turnoverRate', label: 'Turnover' },
                                { key: 'efapUtilization', label: 'EAP utilization' }
                            ].map((opt) => (
                                <button
                                    key={opt.key}
                                    onClick={() => toggleBench(opt.key)}
                                    className={`text-[11px] font-semibold px-3 py-1.5 rounded-md border transition-colors ${
                                        draft.useBenchmark[opt.key]
                                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                                            : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                                    }`}
                                >
                                    {draft.useBenchmark[opt.key] ? 'Using average: ' : 'Use average: '}{opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ============================================================ */}
                {/* READINESS ENGINE — ERI in, attribution index out            */}
                {/* ============================================================ */}
                <div className="bg-[var(--brand-deep)] p-6 md:p-7 rounded-xl shadow-md relative overflow-hidden text-white flex flex-col">
                    <div className="absolute top-0 right-0 p-4 opacity-[0.07]"><Gauge size={140} /></div>

                    <div className="flex items-center gap-2 mb-1 relative z-10">
                        <div className="w-9 h-9 rounded-lg bg-white/10 text-[var(--brand-blue)] flex items-center justify-center">
                            <Lock size={16} strokeWidth={2} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-white">EAS Readiness Engine</h3>
                            <p className="text-[11px] text-blue-200/70 font-medium">
                                {readiness.source === 'live'
                                    ? `This organization's own aggregate signals · ${live.windowDays}-day window · released only at k ≥ 5`
                                    : 'Aggregate platform signals · released only at k ≥ 5'}
                            </p>
                        </div>
                    </div>

                    {readiness.source === 'live' ? (
                        <div className="flex items-center gap-2 mt-4 mb-5 relative z-10 flex-wrap">
                            <SourceBadge source="observed" />
                            <span className="text-[11px] text-blue-200/70">
                                ERI is {readiness.basis === 'eri' ? 'the composite (performance + wellbeing + cognitive)' : 'performance-based until wellbeing screeners reach the warehouse'}
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1 mt-4 mb-5 p-1 bg-white/5 rounded-lg w-fit relative z-10">
                            {Object.keys(ADOPTION_SCENARIOS).map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setScenario(s)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                        scenario === s ? 'bg-[var(--brand-blue)] text-white shadow-sm' : 'text-blue-200/70 hover:text-white'
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                            <span className="px-2 text-[10px] text-blue-200/50 uppercase tracking-wider">modeled scenario</span>
                        </div>
                    )}

                    <div className="space-y-4 relative z-10">
                        {readiness.source === 'live' ? (
                            <>
                                <SignalGauge label="ERI · composite readiness" value={readiness.capacity} />
                                <SignalGauge label="Participation (activation)" value={readiness.activation} suffix="%" />
                                <SignalGauge label="Running Hot share" value={runningHotPct} suffix="%" />
                            </>
                        ) : (
                            <>
                                <SignalGauge label="Capacity score" value={readiness.capacity} />
                                <SignalGauge label="Activation" value={readiness.activation} />
                                <SignalGauge label="Engagement" value={readiness.engagement} />
                            </>
                        )}
                    </div>

                    <div className="mt-5 pt-5 border-t border-white/10 relative z-10">
                        <div className="flex items-end justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--brand-blue)] mb-1">Engagement-Adjusted Attribution Index</p>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-4xl font-semibold text-white tracking-tight">{readiness.readinessIndex.toFixed(2)}×</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 text-[10px] font-medium text-blue-200/60 bg-white/5 border border-white/10 px-2.5 py-1.5 rounded-md">
                                <Lock size={11} /> Proprietary weighting
                            </div>
                        </div>
                        <p className="text-[11px] text-blue-200/60 font-light leading-relaxed mt-3">
                            Stronger aggregate readiness earns the program toward its attribution ceiling — engagement is rewarded, never assumed.
                            The signal inputs are shown; the weighting that converts them into the index is part of the EAS model and isn&apos;t exposed.
                        </p>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* AGAINST THE BUSINESS AVERAGE                                 */}
            {/* ============================================================ */}
            {compare.length > 0 && (
                <div className="bg-white p-6 md:p-7 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><Scale size={18} strokeWidth={2} /></div>
                        <div>
                            <h3 className="text-sm font-bold text-[var(--brand-deep)]">Against the business average</h3>
                            <p className="text-[11px] text-slate-400 font-medium">Where this organization sits relative to the averages the model falls back on</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                        {compare.map((c) => (
                            <div key={c.key} className="p-4 rounded-lg border border-slate-100 bg-slate-50/60">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider truncate">{c.label}</span>
                                    <SourceBadge source={c.source} />
                                </div>
                                <div className="flex items-baseline gap-1.5">
                                    <span className="text-2xl font-bold text-slate-800 tabular-nums">{c.value}</span>
                                    <span className="text-[11px] text-slate-400">{c.unit}</span>
                                </div>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    average {c.average}{c.unit ? ` ${c.unit}` : ''}
                                    {c.deltaPct !== null && c.source !== 'benchmark' && (
                                        <span className={`ml-1.5 font-semibold ${c.better === null ? 'text-slate-500' : c.better ? 'text-emerald-600' : 'text-amber-600'}`}>
                                            {c.deltaPct > 0 ? '+' : ''}{c.deltaPct}%{c.better === null ? '' : c.better ? ' better' : ' worse'}
                                        </span>
                                    )}
                                    {c.source === 'benchmark' && <span className="ml-1.5 text-slate-400">(using the average)</span>}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ============================================================ */}
            {/* ATTRIBUTION DIAL                                             */}
            {/* ============================================================ */}
            <div className="bg-white p-6 md:p-7 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-3 max-w-xl">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                            <Calculator size={18} strokeWidth={2} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-[var(--brand-deep)] mb-1">Attribution discipline</h3>
                            <p className="text-xs text-slate-500 font-light leading-relaxed">
                                Each term is measured against an employer baseline, then discounted by a conservative attribution factor.
                                EAS never claims full causality — the dial is capped at {ATTRIBUTION_MAX}%. Stronger causal claims need a longer window or a matched-cohort comparison.
                            </p>
                        </div>
                    </div>
                    <div className="w-full md:w-72 shrink-0">
                        <div className="flex justify-between text-xs font-semibold text-slate-600 mb-2">
                            <span>Base attribution</span>
                            <span className="text-[var(--brand-deep)]">{draft.attribution}%</span>
                        </div>
                        <input
                            type="range" min={ATTRIBUTION_MIN} max={ATTRIBUTION_MAX} step={1}
                            value={draft.attribution}
                            onChange={(e) => setC('attribution')(num(e.target.value))}
                            className="w-full accent-[var(--brand-blue)]"
                        />
                        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                            <span>{ATTRIBUTION_MIN}% conservative</span>
                            <span>{ATTRIBUTION_MAX}% ceiling</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                            Engagement-adjusted attribution:
                            <strong className="text-[var(--brand-deep)]"> {Math.round(result.effectiveAttribution * 100)}%</strong>
                            {' '}— deliberately discounted so the figure above holds up under a finance review.
                        </p>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* THE FIVE TERMS                                               */}
            {/* ============================================================ */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {terms.map((t) => {
                    const Icon = t.icon;
                    return (
                        <div key={t.key} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
                            <div>
                                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                                    <Icon size={20} strokeWidth={2} />
                                </div>
                                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">{t.label}</h3>
                                <p className="text-3xl font-bold text-slate-800 mb-3 tracking-tight">{compact(t.value)}</p>
                                <p className="text-xs text-slate-500 leading-relaxed font-light mb-4">{t.basis}</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 pt-3 border-t border-slate-100">
                                <ConfidenceTag level={t.level} />
                                <SourceBadge source={t.source} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Presenteeism note */}
            <div className="bg-white p-6 rounded-xl border border-dashed border-slate-300 flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
                <div>
                    <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-1">Presenteeism — excluded from the headline</h3>
                    <p className="text-xs text-slate-500 font-light leading-relaxed">
                        Productivity loss while present is real, but it isn&apos;t added to Net ROI unless the employer uses a validated instrument.
                        Until then it is tracked as a leading indicator only — never claimed as direct savings.
                    </p>
                </div>
            </div>

            {/* ============================================================ */}
            {/* BENCHMARK ASSUMPTIONS (collapsible)                          */}
            {/* ============================================================ */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <button
                    onClick={() => setShowBench((v) => !v)}
                    className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
                >
                    <div className="flex items-center gap-2">
                        <BarChart2 size={18} className="text-[var(--brand-blue)]" />
                        <span className="text-sm font-bold text-[var(--brand-deep)]">Business averages &amp; modeled assumptions</span>
                        <span className="text-[9px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded">Modeled</span>
                    </div>
                    <span className="text-xs font-semibold text-slate-400">{showBench ? 'Hide' : 'Edit'}</span>
                </button>

                {showBench && (
                    <div className="px-6 pb-6 pt-1 border-t border-slate-100">
                        <p className="text-xs text-slate-500 font-light mb-5 mt-4 leading-relaxed max-w-2xl">
                            These default to conservative sector reference values, illustrative until a sourced set for this sector is on file, and are used wherever client baseline data isn&apos;t available.
                            Edits here apply to this session only and are labeled modeled, never measured.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {BENCH_FIELDS.map((b) => (
                                <Field key={b.key} label={b.label} unit={b.unit} prefix={b.prefix} step={b.step}
                                    value={benchOverrides[b.key] ?? bench[b.key]} onChange={setB(b.key)} />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ============================================================ */}
            {/* DISCIPLINE FOOTER                                            */}
            {/* ============================================================ */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 flex flex-col md:flex-row gap-4 items-start">
                <ShieldCheck size={22} className="text-[var(--brand-deep)] shrink-0 mt-0.5" />
                <div className="space-y-3">
                    <p className="text-sm font-semibold text-[var(--brand-deep)]">EAS reports what it measures and labels what it models.</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><ConfidenceTag level="Leading" /> 30–90 days · directly observable</span>
                        <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><ConfidenceTag level="Intermediate" /> 90 days–6 mo · directional</span>
                        <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><ConfidenceTag level="Lagging" /> 6–12 mo+ · needs baseline / cohort</span>
                    </div>
                    <p className="text-xs text-slate-500 font-light leading-relaxed max-w-3xl">
                        This is a measurement framework, not a savings promise. Leading indicators move first; absenteeism, turnover,
                        disability and claims need a longer window and a comparison baseline before any causal claim is made.
                        {isLive ? ' The readiness signals above are this organization’s own; the financial terms are modeled from its inputs.' : ''}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RoiCalculator;
